// src/pages/admin/SponsorWiseList.jsx
import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Users, 
  Phone, 
  Calendar,
  Eye,
  Download,
  UserCheck,
  Star,
  Award,
  Link,
  ChevronUp,
  ChevronDown,
  User,
  DollarSign,
  UserPlus,
  BarChart3,
  FileText,
  ChevronRight,
  Smartphone,
  Monitor,
  X,
  Mail,
  Hash,
  TrendingUp,
  CreditCard,
  Clock,
  Shield,
  Globe,
  Briefcase,
  Key,
  Lock,
  Eye as EyeIcon,
  EyeOff
} from 'lucide-react';
import { database, ref, onValue } from '../../firebaseConfig';

const SponsorWiseList = () => {
  const [sponsors, setSponsors] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    sponsorId: '',
    sponsorName: '',
    memberId: '',
    status: 'all'
  });
  const [sortConfig, setSortConfig] = useState({ key: 'totalDownline', direction: 'desc' });
  const [expandedItems, setExpandedItems] = useState({});
  const [viewMode, setViewMode] = useState('auto');
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPassword, setShowPassword] = useState({ sponsor: false, member: false });

  // Fetch all users from Firebase - Updated for nested date structure
  useEffect(() => {
    const usersRef = ref(database, 'users');
    
    onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      let userList = [];
      
      if (data) {
        // Iterate through each date (e.g., 2026-02-06)
        Object.keys(data).forEach(dateKey => {
          const dateData = data[dateKey];
          
          // Check if dateData is an object (not null or undefined)
          if (dateData && typeof dateData === 'object') {
            // Iterate through users under this date
            Object.keys(dateData).forEach(userKey => {
              const user = dateData[userKey];
              
              // Skip admin users or invalid entries
              if (!user || userKey === 'admin' || user.userId === 'admin' || user.role === 'admin') {
                return;
              }
              
              // Generate member ID if not present
              let memberId = user.memberId || user.userId || `AV${String(userKey).substring(0, 6)}`;
              
              userList.push({
                id: `${dateKey}_${userKey}`,
                firebaseDateKey: dateKey,
                firebaseUserKey: userKey,
                memberId: memberId,
                ...user
              });
            });
          }
        });
      }
      
      console.log(`Found ${userList.length} users`);
      setAllUsers(userList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching users:", error);
      setLoading(false);
    });
  }, []);

  // Process sponsors data
  useEffect(() => {
    if (loading) return;

    // Create maps for quick lookups
    const userMap = {};
    const memberIdMap = {};
    const sponsorCodeMap = {};
    
    allUsers.forEach(user => {
      userMap[user.id] = user;
      if (user.memberId) {
        memberIdMap[user.memberId] = user;
      }
      if (user.sponsorId) {
        sponsorCodeMap[user.sponsorId] = user;
      }
    });

    // Build direct downline map based on uplineSponsorId
    const directDownlineMap = {};
    allUsers.forEach(user => {
      if (user.uplineSponsorId && user.uplineSponsorId !== 'N/A') {
        const sponsorCode = user.uplineSponsorId;
        if (!directDownlineMap[sponsorCode]) {
          directDownlineMap[sponsorCode] = [];
        }
        directDownlineMap[sponsorCode].push(user);
      }
    });

    // Calculate total downline recursively
    const calculateTotalDownline = (sponsorCode, visited = new Set()) => {
      if (visited.has(sponsorCode)) return 0;
      visited.add(sponsorCode);
      
      const directDownlines = directDownlineMap[sponsorCode] || [];
      let total = directDownlines.length;
      
      // Recursively count downlines of downlines
      directDownlines.forEach(downline => {
        if (downline.sponsorId && downline.sponsorId !== sponsorCode) {
          total += calculateTotalDownline(downline.sponsorId, visited);
        }
      });
      
      return total;
    };

    // Calculate total business (sum of investments/packages)
    const calculateTotalBusiness = (sponsorCode, visited = new Set()) => {
      if (visited.has(sponsorCode)) return 0;
      visited.add(sponsorCode);
      
      const directDownlines = directDownlineMap[sponsorCode] || [];
      let totalBusiness = 0;
      
      // Add direct downlines' business
      directDownlines.forEach(downline => {
        // Calculate downline's total investment from packages
        let downlineInvestment = 0;
        if (downline.packages && Array.isArray(downline.packages)) {
          downline.packages.forEach(pkg => {
            const amount = parseFloat(pkg.baseAmount) || parseFloat(pkg.investmentAmount) || 0;
            downlineInvestment += amount;
          });
        }
        
        // Add totalInvestment if available
        if (downline.totalInvestment) {
          downlineInvestment = Math.max(downlineInvestment, parseFloat(downline.totalInvestment) || 0);
        }
        
        totalBusiness += downlineInvestment;
        
        // Recursively add business from their downlines
        if (downline.sponsorId && downline.sponsorId !== sponsorCode) {
          totalBusiness += calculateTotalBusiness(downline.sponsorId, visited);
        }
      });
      
      return totalBusiness;
    };

    // Build sponsors list
    const sponsorsList = [];
    
    // Find all unique sponsors
    const uniqueSponsors = new Set();
    allUsers.forEach(user => {
      if (user.sponsorId && user.sponsorId !== 'N/A' && user.sponsorId !== '') {
        uniqueSponsors.add(user.sponsorId);
      }
    });

    // Process each unique sponsor
    uniqueSponsors.forEach(sponsorCode => {
      const sponsorUser = sponsorCodeMap[sponsorCode];
      if (!sponsorUser) {
        // Sponsor not found in users - create a placeholder
        sponsorsList.push({
          id: sponsorCode,
          sponsorId: sponsorCode,
          sponsorName: 'Unknown Sponsor',
          sponsorMobile: 'N/A',
          sponsorEmail: 'N/A',
          sponsorStatus: 'unknown',
          sponsorPassword: '••••••••',
          memberId: 'N/A',
          memberName: 'Sponsor Not Found',
          memberMobile: 'N/A',
          memberPassword: '••••••••',
          joinDate: 'N/A',
          directDownline: 0,
          totalDownline: 0,
          totalBusiness: 0,
          commissionEarned: 0,
          status: 'unknown',
          sponsorData: null,
          memberData: null
        });
        return;
      }

      const directDownlines = directDownlineMap[sponsorCode] || [];
      const totalDownline = calculateTotalDownline(sponsorCode);
      const totalBusiness = calculateTotalBusiness(sponsorCode);
      
      // Calculate commission earned from sponsor user
      const commissionEarned = parseFloat(sponsorUser.totalEarnings || sponsorUser.earnings || 0);
      
      // Create entry for each direct downline
      if (directDownlines.length > 0) {
        directDownlines.forEach(downline => {
          // Generate downline member ID
          const downlineMemberId = downline.memberId || `AV${String(downline.firebaseUserKey).substring(0, 6)}`;
          
          sponsorsList.push({
            id: `${sponsorCode}-${downline.id}`,
            sponsorId: sponsorCode,
            sponsorName: sponsorUser.name || sponsorUser.fullName || 'Unknown Sponsor',
            sponsorMobile: sponsorUser.mobile || sponsorUser.phone || 'N/A',
            sponsorEmail: sponsorUser.email || 'N/A',
            sponsorStatus: sponsorUser.status || 'active',
            sponsorPassword: sponsorUser.password || '••••••••',
            memberId: downlineMemberId,
            memberName: downline.name || downline.fullName || 'Unknown Member',
            memberMobile: downline.mobile || downline.phone || 'N/A',
            memberPassword: downline.password || '••••••••',
            joinDate: downline.registrationDate || downline.createdAt || downline.firebaseDateKey || 'N/A',
            directDownline: directDownlines.length,
            totalDownline: totalDownline,
            totalBusiness: totalBusiness,
            commissionEarned: commissionEarned,
            status: downline.status || 'active',
            sponsorData: sponsorUser,
            memberData: downline
          });
        });
      } else {
        // If sponsor has no direct downlines, still include them
        sponsorsList.push({
          id: sponsorCode,
          sponsorId: sponsorCode,
          sponsorName: sponsorUser.name || sponsorUser.fullName || 'Unknown Sponsor',
          sponsorMobile: sponsorUser.mobile || sponsorUser.phone || 'N/A',
          sponsorEmail: sponsorUser.email || 'N/A',
          sponsorStatus: sponsorUser.status || 'active',
          sponsorPassword: sponsorUser.password || '••••••••',
          memberId: 'N/A',
          memberName: 'No Direct Downline',
          memberMobile: 'N/A',
          memberPassword: '••••••••',
          joinDate: 'N/A',
          directDownline: 0,
          totalDownline: 0,
          totalBusiness: 0,
          commissionEarned: commissionEarned,
          status: sponsorUser.status || 'active',
          sponsorData: sponsorUser,
          memberData: null
        });
      }
    });

    console.log(`Found ${sponsorsList.length} sponsor entries`);
    setSponsors(sponsorsList);
  }, [loading, allUsers]);

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatCurrencyWithDecimal = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleExpanded = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleViewSponsor = (sponsor) => {
    setSelectedSponsor(sponsor);
    setShowDetailsModal(true);
    setShowPassword({ sponsor: false, member: false });
  };

  const closeModal = () => {
    setShowDetailsModal(false);
    setSelectedSponsor(null);
    setShowPassword({ sponsor: false, member: false });
  };

  const togglePasswordVisibility = (type) => {
    setShowPassword(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Filter and sort sponsors
  const filteredSponsors = sponsors.filter(sponsor => {
    const matchesSponsorId = !filters.sponsorId || 
      sponsor.sponsorId.toLowerCase().includes(filters.sponsorId.toLowerCase());
    
    const matchesSponsorName = !filters.sponsorName || 
      sponsor.sponsorName.toLowerCase().includes(filters.sponsorName.toLowerCase());
    
    const matchesMemberId = !filters.memberId || 
      sponsor.memberId.toLowerCase().includes(filters.memberId.toLowerCase());
    
    const matchesStatus = filters.status === 'all' || 
      sponsor.status.toLowerCase() === filters.status.toLowerCase();
    
    return matchesSponsorId && matchesSponsorName && matchesMemberId && matchesStatus;
  }).sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    return sortConfig.direction === 'asc' 
      ? (aValue || 0) - (bValue || 0)
      : (bValue || 0) - (aValue || 0);
  });

  // Get unique sponsors for stats
  const uniqueSponsorsMap = {};
  filteredSponsors.forEach(sponsor => {
    if (!uniqueSponsorsMap[sponsor.sponsorId]) {
      uniqueSponsorsMap[sponsor.sponsorId] = sponsor;
    }
  });
  const uniqueSponsors = Object.values(uniqueSponsorsMap);

  const totalSponsors = uniqueSponsors.length;
  const activeSponsors = uniqueSponsors.filter(s => s.sponsorStatus === 'active').length;
  const totalCommission = uniqueSponsors.reduce((sum, s) => sum + (s.commissionEarned || 0), 0);
  const totalBusiness = uniqueSponsors.reduce((sum, s) => sum + (s.totalBusiness || 0), 0);

  const exportToCSV = () => {
    const headers = [
      'Sponsor ID', 'Sponsor Name', 'Sponsor Mobile', 'Sponsor Email', 'Sponsor Password',
      'Member ID', 'Member Name', 'Join Date', 'Direct Downline',
      'Total Downline', 'Total Business', 'Commission Earned', 'Status'
    ];
    
    const csvData = filteredSponsors.map(sponsor => [
      sponsor.sponsorId,
      sponsor.sponsorName,
      sponsor.sponsorMobile,
      sponsor.sponsorEmail,
      sponsor.sponsorPassword,
      sponsor.memberId,
      sponsor.memberName,
      formatDate(sponsor.joinDate),
      sponsor.directDownline,
      sponsor.totalDownline,
      sponsor.totalBusiness,
      sponsor.commissionEarned,
      sponsor.status
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sponsors_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status) => {
    const isActive = status === 'active';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        isActive 
          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
          : 'bg-red-500/20 text-red-400 border border-red-500/30'
      }`}>
        {isActive ? (
          <>
            <UserCheck className="h-3 w-3 mr-1" />
            Active
          </>
        ) : 'Inactive'}
      </span>
    );
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUp className="h-3 w-3 opacity-30" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="h-3 w-3" />
      : <ChevronDown className="h-3 w-3" />;
  };

  // Mobile Accordion Item Component
  const MobileSponsorCard = ({ sponsor, index }) => {
    const isExpanded = expandedItems[sponsor.id];
    
    return (
      <div className="border-glow bg-secondary/30 border border-border rounded-xl overflow-hidden mb-3">
        {/* Card Header */}
        <div 
          className="p-4 cursor-pointer hover:bg-secondary/50 transition-colors"
          onClick={() => toggleExpanded(sponsor.id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="text-muted-foreground text-sm font-medium mr-3 font-body">
                #{index + 1}
              </div>
              <div>
                <div className="font-semibold text-foreground text-sm mb-0.5 font-heading">
                  {sponsor.sponsorName}
                </div>
                <div className="font-mono font-medium text-primary text-xs bg-primary/10 px-2 py-0.5 rounded inline-block font-body">
                  {sponsor.sponsorId}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge(sponsor.status)}
              <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${
                isExpanded ? 'rotate-90' : ''
              }`} />
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="bg-secondary/50 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-foreground font-heading">
                {sponsor.directDownline}
              </div>
              <div className="text-xs text-muted-foreground font-body">Direct</div>
            </div>
            <div className="bg-secondary/50 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-electric font-heading">
                {sponsor.totalDownline}
              </div>
              <div className="text-xs text-muted-foreground font-body">Total</div>
            </div>
          </div>
        </div>
        
        {/* Expanded Content */}
        {isExpanded && (
          <div className="p-4 pt-0 border-t border-border/50">
            <div className="space-y-4">
              {/* Sponsor Details */}
              <div>
                <div className="text-xs text-muted-foreground mb-2 font-heading">SPONSOR DETAILS</div>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <User className="h-3.5 w-3.5 text-muted-foreground mr-2" />
                    <span className="text-sm text-foreground font-body">{sponsor.sponsorName}</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="h-3.5 w-3.5 text-muted-foreground mr-2" />
                    <span className="text-sm text-foreground font-body">{sponsor.sponsorMobile}</span>
                  </div>
                  <div className="text-sm text-muted-foreground truncate font-body">
                    {sponsor.sponsorEmail}
                  </div>
                </div>
              </div>
              
              {/* Member Details */}
              <div>
                <div className="text-xs text-muted-foreground mb-2 font-heading">MEMBER DETAILS</div>
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-foreground font-heading">
                    {sponsor.memberName}
                  </div>
                  <div className="font-mono text-xs text-muted-foreground bg-secondary px-2 py-1 rounded font-body">
                    ID: {sponsor.memberId}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground mr-2" />
                    <span className="text-sm text-foreground font-body">
                      Joined: {formatDate(sponsor.joinDate)}
                    </span>
                  </div>
                  {sponsor.memberMobile !== 'N/A' && (
                    <div className="flex items-center">
                      <Phone className="h-3.5 w-3.5 text-muted-foreground mr-2" />
                      <span className="text-sm text-foreground font-body">{sponsor.memberMobile}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Business Stats */}
              <div>
                <div className="text-xs text-muted-foreground mb-2 font-heading">BUSINESS STATS</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-secondary/50 rounded-lg p-2 text-center">
                    <div className="text-sm text-muted-foreground mb-0.5 font-body">Total Business</div>
                    <div className="text-base font-bold text-yellow-400 font-heading">
                      {formatCurrency(sponsor.totalBusiness)}
                    </div>
                  </div>
                  <div className="bg-secondary/50 rounded-lg p-2 text-center">
                    <div className="text-sm text-muted-foreground mb-0.5 font-body">Commission</div>
                    <div className="text-base font-bold text-purple-400 font-heading">
                      {formatCurrency(sponsor.commissionEarned)}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewSponsor(sponsor);
                  }}
                  className="flex-1 btn-electric py-2 text-sm rounded-lg transition-all font-body"
                >
                  <Eye className="h-3.5 w-3.5 inline mr-1.5" />
                  View Full Details
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Detailed Sponsor Modal Component
  const SponsorDetailsModal = () => {
    if (!selectedSponsor) return null;

    const sponsor = selectedSponsor;
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-secondary border-glow border border-primary/20 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          {/* Modal Header */}
          <div className="p-5 border-b border-border bg-gradient-to-r from-primary/10 to-primary/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="icon-electric mr-3">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-foreground font-heading">
                    Sponsor Network Details
                  </h2>
                  <p className="text-muted-foreground text-sm font-body">
                    Complete information about sponsor and member
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-secondary/80 rounded-xl transition-colors"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Modal Content */}
          <div className="p-5 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="space-y-6">
              {/* Sponsor Information Section */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center font-heading">
                  <Briefcase className="h-5 w-5 mr-2 text-primary" />
                  Sponsor Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="bg-secondary/50 rounded-xl p-4">
                      <div className="flex items-center mb-2">
                        <User className="h-4 w-4 text-muted-foreground mr-2" />
                        <span className="text-sm text-muted-foreground font-heading">Full Name</span>
                      </div>
                      <div className="text-base font-semibold text-foreground font-heading">
                        {sponsor.sponsorName}
                      </div>
                    </div>
                    
                    <div className="bg-secondary/50 rounded-xl p-4">
                      <div className="flex items-center mb-2">
                        <Hash className="h-4 w-4 text-muted-foreground mr-2" />
                        <span className="text-sm text-muted-foreground font-heading">Sponsor ID</span>
                      </div>
                      <div className="font-mono font-bold text-primary text-lg font-body">
                        {sponsor.sponsorId}
                      </div>
                    </div>
                    
                    <div className="bg-secondary/50 rounded-xl p-4">
                      <div className="flex items-center mb-2">
                        <Key className="h-4 w-4 text-muted-foreground mr-2" />
                        <span className="text-sm text-muted-foreground font-heading">Password</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="font-mono text-base text-foreground font-body">
                          {showPassword.sponsor ? sponsor.sponsorPassword : '••••••••'}
                        </div>
                        <button
                          onClick={() => togglePasswordVisibility('sponsor')}
                          className="text-primary hover:text-primary/80 transition-colors"
                          title={showPassword.sponsor ? 'Hide Password' : 'Show Password'}
                        >
                          {showPassword.sponsor ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <EyeIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-secondary/50 rounded-xl p-4">
                      <div className="flex items-center mb-2">
                        <Phone className="h-4 w-4 text-muted-foreground mr-2" />
                        <span className="text-sm text-muted-foreground font-heading">Mobile Number</span>
                      </div>
                      <div className="text-base font-semibold text-foreground font-heading">
                        {sponsor.sponsorMobile}
                      </div>
                    </div>
                    
                    <div className="bg-secondary/50 rounded-xl p-4">
                      <div className="flex items-center mb-2">
                        <Mail className="h-4 w-4 text-muted-foreground mr-2" />
                        <span className="text-sm text-muted-foreground font-heading">Email Address</span>
                      </div>
                      <div className="text-base font-semibold text-foreground font-heading truncate">
                        {sponsor.sponsorEmail}
                      </div>
                    </div>
                    
                    <div className="bg-secondary/50 rounded-xl p-4">
                      <div className="flex items-center mb-2">
                        <Shield className="h-4 w-4 text-muted-foreground mr-2" />
                        <span className="text-sm text-muted-foreground font-heading">Status</span>
                      </div>
                      {getStatusBadge(sponsor.sponsorStatus)}
                    </div>
                    
                    {sponsor.sponsorData?.country && (
                      <div className="bg-secondary/50 rounded-xl p-4">
                        <div className="flex items-center mb-2">
                          <Globe className="h-4 w-4 text-muted-foreground mr-2" />
                          <span className="text-sm text-muted-foreground font-heading">Country</span>
                        </div>
                        <div className="text-base font-semibold text-foreground font-heading">
                          {sponsor.sponsorData.country}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Member Information Section */}
              {sponsor.memberData && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center font-heading">
                    <Users className="h-5 w-5 mr-2 text-green-500" />
                    Direct Downline Member
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="bg-secondary/50 rounded-xl p-4">
                        <div className="flex items-center mb-2">
                          <User className="h-4 w-4 text-muted-foreground mr-2" />
                          <span className="text-sm text-muted-foreground font-heading">Member Name</span>
                        </div>
                        <div className="text-base font-semibold text-foreground font-heading">
                          {sponsor.memberName}
                        </div>
                      </div>
                      
                      <div className="bg-secondary/50 rounded-xl p-4">
                        <div className="flex items-center mb-2">
                          <Hash className="h-4 w-4 text-muted-foreground mr-2" />
                          <span className="text-sm text-muted-foreground font-heading">Member ID</span>
                        </div>
                        <div className="font-mono font-bold text-green-500 text-lg font-body">
                          {sponsor.memberId}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="bg-secondary/50 rounded-xl p-4">
                        <div className="flex items-center mb-2">
                          <Key className="h-4 w-4 text-muted-foreground mr-2" />
                          <span className="text-sm text-muted-foreground font-heading">Password</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="font-mono text-base text-foreground font-body">
                            {showPassword.member ? sponsor.memberPassword : '••••••••'}
                          </div>
                          <button
                            onClick={() => togglePasswordVisibility('member')}
                            className="text-primary hover:text-primary/80 transition-colors"
                            title={showPassword.member ? 'Hide Password' : 'Show Password'}
                          >
                            {showPassword.member ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <EyeIcon className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      
                      <div className="bg-secondary/50 rounded-xl p-4">
                        <div className="flex items-center mb-2">
                          <Phone className="h-4 w-4 text-muted-foreground mr-2" />
                          <span className="text-sm text-muted-foreground font-heading">Member Mobile</span>
                        </div>
                        <div className="text-base font-semibold text-foreground font-heading">
                          {sponsor.memberMobile}
                        </div>
                      </div>
                      
                      <div className="bg-secondary/50 rounded-xl p-4">
                        <div className="flex items-center mb-2">
                          <Calendar className="h-4 w-4 text-muted-foreground mr-2" />
                          <span className="text-sm text-muted-foreground font-heading">Join Date</span>
                        </div>
                        <div className="text-base font-semibold text-foreground font-heading">
                          {formatDate(sponsor.joinDate)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Information */}
              {sponsor.sponsorData && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center font-heading">
                    <FileText className="h-5 w-5 mr-2 text-blue-500" />
                    Additional Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {sponsor.sponsorData.registrationDate && (
                      <div className="bg-secondary/50 rounded-xl p-4">
                        <div className="flex items-center mb-2">
                          <Clock className="h-4 w-4 text-muted-foreground mr-2" />
                          <span className="text-sm text-muted-foreground font-heading">Registration Date</span>
                        </div>
                        <div className="text-base font-semibold text-foreground font-heading">
                          {formatDate(sponsor.sponsorData.registrationDate)}
                        </div>
                      </div>
                    )}
                    
                    {sponsor.sponsorData.lastLogin && (
                      <div className="bg-secondary/50 rounded-xl p-4">
                        <div className="flex items-center mb-2">
                          <Clock className="h-4 w-4 text-muted-foreground mr-2" />
                          <span className="text-sm text-muted-foreground font-heading">Last Login</span>
                        </div>
                        <div className="text-base font-semibold text-foreground font-heading">
                          {formatDate(sponsor.sponsorData.lastLogin)}
                        </div>
                      </div>
                    )}
                    
                    {sponsor.sponsorData.totalInvestment !== undefined && sponsor.sponsorData.totalInvestment > 0 && (
                      <div className="bg-secondary/50 rounded-xl p-4">
                        <div className="flex items-center mb-2">
                          <DollarSign className="h-4 w-4 text-muted-foreground mr-2" />
                          <span className="text-sm text-muted-foreground font-heading">Total Investment</span>
                        </div>
                        <div className="text-lg font-bold text-green-400 font-heading">
                          ${parseFloat(sponsor.sponsorData.totalInvestment).toFixed(2)}
                        </div>
                      </div>
                    )}
                    
                    {sponsor.sponsorData.referralCount !== undefined && (
                      <div className="bg-secondary/50 rounded-xl p-4">
                        <div className="flex items-center mb-2">
                          <Users className="h-4 w-4 text-muted-foreground mr-2" />
                          <span className="text-sm text-muted-foreground font-heading">Referral Count</span>
                        </div>
                        <div className="text-lg font-bold text-blue-400 font-heading">
                          {sponsor.sponsorData.referralCount || 0}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Network Statistics */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center font-heading">
                  <TrendingUp className="h-5 w-5 mr-2 text-purple-500" />
                  Network Statistics
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-secondary/50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-electric font-heading mb-1">
                      {sponsor.directDownline}
                    </div>
                    <div className="text-sm text-muted-foreground font-heading">Direct Downlines</div>
                  </div>
                  
                  <div className="bg-secondary/50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-yellow-400 font-heading mb-1">
                      {sponsor.totalDownline}
                    </div>
                    <div className="text-sm text-muted-foreground font-heading">Total Downline</div>
                  </div>
                  
                  <div className="bg-secondary/50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-green-400 font-heading mb-1">
                      {formatCurrency(sponsor.totalBusiness)}
                    </div>
                    <div className="text-sm text-muted-foreground font-heading">Total Business</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="p-4 border-t border-border bg-secondary/80">
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-5 py-2.5 text-sm bg-secondary hover:bg-secondary/80 text-foreground rounded-xl transition-colors border border-border flex items-center justify-center font-body"
              >
                <X className="h-4 w-4 mr-2" />
                Close
              </button>
              <button
                onClick={() => {
                  // Copy details to clipboard
                  const details = `Sponsor Details:\n` +
                    `ID: ${sponsor.sponsorId}\n` +
                    `Name: ${sponsor.sponsorName}\n` +
                    `Mobile: ${sponsor.sponsorMobile}\n` +
                    `Email: ${sponsor.sponsorEmail}\n` +
                    `Password: ${sponsor.sponsorPassword}\n` +
                    `Status: ${sponsor.sponsorStatus}\n` +
                    `Direct Downlines: ${sponsor.directDownline}\n` +
                    `Total Downline: ${sponsor.totalDownline}\n` +
                    `Total Business: ${formatCurrency(sponsor.totalBusiness)}\n\n` +
                    `Member Details:\n` +
                    `ID: ${sponsor.memberId}\n` +
                    `Name: ${sponsor.memberName}\n` +
                    `Mobile: ${sponsor.memberMobile}\n` +
                    `Password: ${sponsor.memberPassword}\n` +
                    `Join Date: ${formatDate(sponsor.joinDate)}`;
                  
                  navigator.clipboard.writeText(details).then(() => {
                    alert('Details copied to clipboard!');
                  });
                }}
                className="px-5 py-2.5 text-sm btn-electric rounded-xl transition-all flex items-center justify-center font-body"
              >
                <FileText className="h-4 w-4 mr-2" />
                Copy Details
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto pt-10 px-4">
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground text-sm font-body">Loading sponsors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto pt-8 px-3">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center font-heading">
              <Users className="h-6 w-6 mr-3 text-primary" />
              Sponsor Network Analytics
            </h1>
            <p className="text-muted-foreground mt-1.5 text-sm font-body">
              {totalSponsors} unique sponsors • {activeSponsors} active • {filteredSponsors.length} entries
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="hidden sm:flex items-center gap-1 bg-secondary/50 rounded-lg p-1">
              <button
                onClick={() => setViewMode('auto')}
                className={`px-3 py-1.5 text-sm rounded-md transition-all flex items-center ${
                  viewMode === 'auto' 
                    ? 'bg-primary text-white' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <span className="hidden xs:inline mr-1.5">Auto</span>
                <BarChart3 className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setViewMode('desktop')}
                className={`px-3 py-1.5 text-sm rounded-md transition-all flex items-center ${
                  viewMode === 'desktop' 
                    ? 'bg-primary text-white' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <span className="hidden xs:inline mr-1.5">Table</span>
                <Monitor className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setViewMode('mobile')}
                className={`px-3 py-1.5 text-sm rounded-md transition-all flex items-center ${
                  viewMode === 'mobile' 
                    ? 'bg-primary text-white' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <span className="hidden xs:inline mr-1.5">Cards</span>
                <Smartphone className="h-3.5 w-3.5" />
              </button>
            </div>
            
            <button
              onClick={exportToCSV}
              disabled={filteredSponsors.length === 0}
              className={`btn-electric px-4 py-2.5 rounded-lg transition-all flex items-center text-sm border-glow ${
                filteredSponsors.length === 0
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
            >
              <Download className="h-4 w-4 mr-2" />
              <span className="hidden xs:inline">Export CSV</span>
              <span className="xs:hidden">Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="card-glow p-4 col-span-2 lg:col-span-1">
          <div className="flex items-center">
            <div className="icon-electric mr-3">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="text-muted-foreground text-xs mb-0.5 font-heading">Total Sponsors</div>
              <div className="text-xl font-bold text-foreground font-heading">{totalSponsors}</div>
            </div>
          </div>
        </div>

        <div className="card-glow p-4 col-span-2 lg:col-span-1">
          <div className="flex items-center">
            <div className="icon-electric mr-3">
              <UserCheck className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <div className="text-muted-foreground text-xs mb-0.5 font-heading">Active Sponsors</div>
              <div className="text-xl font-bold text-green-400 font-heading">{activeSponsors}</div>
            </div>
          </div>
        </div>

        <div className="card-glow p-4 col-span-2 lg:col-span-1">
          <div className="flex items-center">
            <div className="icon-electric mr-3">
              <DollarSign className="h-5 w-5 text-yellow-500" />
            </div>
            <div>
              <div className="text-muted-foreground text-xs mb-0.5 font-heading">Total Business</div>
              <div className="text-xl font-bold text-yellow-400 font-heading">
                {formatCurrency(totalBusiness)}
              </div>
            </div>
          </div>
        </div>

        <div className="card-glow p-4 col-span-2 lg:col-span-1">
          <div className="flex items-center">
            <div className="icon-electric mr-3">
              <Award className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <div className="text-muted-foreground text-xs mb-0.5 font-heading">Commission</div>
              <div className="text-xl font-bold text-purple-400 font-heading">
                {formatCurrency(totalCommission)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="card-glow p-4 sm:p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-primary" />
          <h2 className="text-base font-semibold text-foreground font-heading">Filter Sponsors</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="block text-foreground mb-1.5 text-sm font-heading">Sponsor ID</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                name="sponsorId"
                value={filters.sponsorId}
                onChange={handleFilterChange}
                placeholder="Search by Sponsor ID"
                className="w-full text-sm pl-10 pr-3 py-2.5 bg-secondary/80 border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-body"
              />
            </div>
          </div>

          <div>
            <label className="block text-foreground mb-1.5 text-sm font-heading">Sponsor Name</label>
            <input
              type="text"
              name="sponsorName"
              value={filters.sponsorName}
              onChange={handleFilterChange}
              placeholder="Search by name"
              className="w-full text-sm px-3 py-2.5 bg-secondary/80 border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-body"
            />
          </div>

          <div>
            <label className="block text-foreground mb-1.5 text-sm font-heading">Member ID</label>
            <input
              type="text"
              name="memberId"
              value={filters.memberId}
              onChange={handleFilterChange}
              placeholder="Search by Member ID"
              className="w-full text-sm px-3 py-2.5 bg-secondary/80 border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-body"
            />
          </div>

          <div>
            <label className="block text-foreground mb-1.5 text-sm font-heading">Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-full text-sm px-3 py-2.5 bg-secondary/80 border border-border rounded-xl text-foreground focus:outline-none focus:border-primary font-body"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
          <span className="text-sm text-muted-foreground font-body text-center sm:text-left">
            Showing {filteredSponsors.length} of {sponsors.length} entries
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setFilters({
                  sponsorId: '',
                  sponsorName: '',
                  memberId: '',
                  status: 'all'
                });
                setExpandedItems({});
              }}
              className="px-3 py-1.5 text-sm bg-secondary hover:bg-secondary/80 text-foreground rounded-lg transition-colors border border-border flex items-center font-body"
            >
              <Filter className="h-3.5 w-3.5 mr-1.5" />
              Clear Filters
            </button>
            <button
              onClick={() => {
                const allExpanded = {};
                filteredSponsors.forEach(sponsor => {
                  allExpanded[sponsor.id] = true;
                });
                setExpandedItems(allExpanded);
              }}
              className="px-3 py-1.5 text-sm bg-secondary hover:bg-secondary/80 text-foreground rounded-lg transition-colors border border-border flex items-center font-body sm:hidden"
            >
              <ChevronDown className="h-3.5 w-3.5 mr-1.5" />
              Expand All
            </button>
          </div>
        </div>
      </div>

      {/* Top Sponsors Card */}
      {uniqueSponsors.length > 0 && (
        <div className="card-glow p-4 sm:p-5 mb-6">
          <h2 className="text-base font-semibold text-foreground mb-4 flex items-center font-heading">
            <Star className="h-5 w-5 mr-2 text-yellow-400" />
            Top Sponsors by Network
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {uniqueSponsors
              .sort((a, b) => b.totalDownline - a.totalDownline)
              .slice(0, 5)
              .map((sponsor, index) => (
                <div key={sponsor.sponsorId} className="border-glow bg-secondary/30 border border-border rounded-lg p-3 text-center hover:border-primary/50 transition-colors">
                  <div className={`inline-flex items-center justify-center w-7 h-7 rounded-full mb-2 text-xs font-bold ${
                    index === 0 ? 'bg-gradient-to-br from-yellow-500 to-yellow-600 text-white' :
                    index === 1 ? 'bg-gradient-to-br from-gray-400 to-gray-500 text-white' :
                    index === 2 ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white' :
                    'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="font-semibold text-foreground text-sm truncate mb-0.5 font-heading">
                    {sponsor.sponsorName}
                  </div>
                  <div className="text-muted-foreground text-xs truncate mb-2 font-body">
                    {sponsor.sponsorId}
                  </div>
                  <div className="text-xl font-bold text-electric mb-0.5 font-heading">
                    {sponsor.totalDownline}
                  </div>
                  <div className="text-xs text-muted-foreground font-body">Downline</div>
                  <div className="mt-1 text-xs text-muted-foreground truncate font-body">
                    {formatCurrency(sponsor.totalBusiness)}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Desktop Table View - Hidden on mobile when in auto mode */}
      <div className={`${viewMode === 'mobile' ? 'hidden' : ''} ${viewMode === 'auto' ? 'hidden lg:block' : ''}`}>
        <div className="card-glow overflow-hidden">
          <div className="p-4 sm:p-5 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground flex items-center font-heading">
              <UserPlus className="h-5 w-5 mr-2 text-primary" />
              Sponsor Network List
              <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-body">
                {filteredSponsors.length} entries
              </span>
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50">
                <tr className="text-xs font-heading">
                  <th className="py-3 px-4 text-left text-muted-foreground font-semibold w-12">#</th>
                  <th 
                    className="py-3 px-4 text-left text-muted-foreground font-semibold cursor-pointer hover:text-foreground w-32"
                    onClick={() => handleSort('sponsorId')}
                  >
                    <div className="flex items-center">
                      SPONSOR ID
                      <SortIcon columnKey="sponsorId" />
                    </div>
                  </th>
                  <th 
                    className="py-3 px-4 text-left text-muted-foreground font-semibold cursor-pointer hover:text-foreground w-48"
                    onClick={() => handleSort('sponsorName')}
                  >
                    <div className="flex items-center">
                      SPONSOR INFORMATION
                      <SortIcon columnKey="sponsorName" />
                    </div>
                  </th>
                  <th className="py-3 px-4 text-left text-muted-foreground font-semibold w-48">MEMBER INFORMATION</th>
                  <th 
                    className="py-3 px-4 text-left text-muted-foreground font-semibold cursor-pointer hover:text-foreground w-20 text-center"
                    onClick={() => handleSort('directDownline')}
                  >
                    <div className="flex items-center justify-center">
                      DIRECT
                      <SortIcon columnKey="directDownline" />
                    </div>
                  </th>
                  <th 
                    className="py-3 px-4 text-left text-muted-foreground font-semibold cursor-pointer hover:text-foreground w-20 text-center"
                    onClick={() => handleSort('totalDownline')}
                  >
                    <div className="flex items-center justify-center">
                      TOTAL
                      <SortIcon columnKey="totalDownline" />
                    </div>
                  </th>
                  <th className="py-3 px-4 text-left text-muted-foreground font-semibold w-24">STATUS</th>
                  <th className="py-3 px-4 text-left text-muted-foreground font-semibold w-24">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {filteredSponsors.map((sponsor, index) => (
                  <tr key={sponsor.id} className="hover:bg-secondary/20 transition-colors">
                    <td className="py-3 px-4 text-muted-foreground text-sm font-medium font-body">
                      {index + 1}
                    </td>

                    <td className="py-3 px-4">
                      <div className="font-mono font-semibold text-foreground text-sm font-body bg-primary/10 px-2 py-1 rounded inline-block">
                        {sponsor.sponsorId}
                      </div>
                    </td>

                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="icon-electric mr-3 flex-shrink-0">
                          <User className="h-4 w-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-foreground text-sm truncate mb-0.5 font-heading">
                            {sponsor.sponsorName}
                          </div>
                          <div className="text-muted-foreground text-xs truncate mb-0.5 flex items-center font-body">
                            <Phone className="h-3 w-3 mr-1.5 flex-shrink-0" />
                            {sponsor.sponsorMobile}
                          </div>
                          <div className="text-muted-foreground text-xs truncate font-body">
                            {sponsor.sponsorEmail}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="py-3 px-4">
                      <div className="space-y-1.5">
                        <div className="font-semibold text-foreground text-sm truncate font-heading">
                          {sponsor.memberName}
                        </div>
                        <div className="text-muted-foreground text-xs font-mono bg-secondary px-2 py-1 rounded inline-block font-body">
                          ID: {sponsor.memberId}
                        </div>
                        <div className="text-muted-foreground text-xs flex items-center font-body">
                          <Calendar className="h-3 w-3 mr-1.5 flex-shrink-0" />
                          Joined: {formatDate(sponsor.joinDate)}
                        </div>
                      </div>
                    </td>

                    <td className="py-3 px-4 text-center">
                      <div className="text-xl font-bold text-foreground font-heading">
                        {sponsor.directDownline}
                      </div>
                      <div className="text-xs text-muted-foreground font-body">Direct</div>
                    </td>

                    <td className="py-3 px-4 text-center">
                      <div className="text-xl font-bold text-electric font-heading">
                        {sponsor.totalDownline}
                      </div>
                      <div className="text-xs text-muted-foreground font-body">Total</div>
                    </td>

                    <td className="py-3 px-4">
                      {getStatusBadge(sponsor.status)}
                    </td>

                    <td className="py-3 px-4">
                      <button
                        onClick={() => handleViewSponsor(sponsor)}
                        className="btn-electric px-3 py-1.5 text-sm rounded-lg transition-all flex items-center font-body"
                      >
                        <Eye className="h-3.5 w-3.5 mr-1.5" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredSponsors.length === 0 && !loading && (
              <div className="text-center py-12">
                <div className="text-muted-foreground text-4xl mb-4">🤝</div>
                <h3 className="text-lg font-semibold text-foreground mb-2 font-heading">
                  No Sponsors Found
                </h3>
                <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto font-body">
                  {sponsors.length === 0 
                    ? 'No sponsor data found in the database. Sponsors will appear here once users register.' 
                    : 'Try adjusting your search filters to find what you\'re looking for.'}
                </p>
                {sponsors.length > 0 && (
                  <button
                    onClick={() => setFilters({
                      sponsorId: '',
                      sponsorName: '',
                      memberId: '',
                      status: 'all'
                    })}
                    className="btn-electric px-4 py-2.5 text-sm rounded-lg transition-all font-body"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Accordion View - Hidden on desktop when in auto mode */}
      <div className={`${viewMode === 'desktop' ? 'hidden' : ''} ${viewMode === 'auto' ? 'lg:hidden' : ''}`}>
        <div className="card-glow overflow-hidden">
          <div className="p-4 sm:p-5 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground flex items-center font-heading">
              <UserPlus className="h-5 w-5 mr-2 text-primary" />
              Sponsor Network
              <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-body">
                {filteredSponsors.length} entries
              </span>
            </h2>
            <p className="text-muted-foreground text-sm mt-1 font-body">
              Tap on any card to expand details
            </p>
          </div>
          
          <div className="p-3">
            {filteredSponsors.map((sponsor, index) => (
              <MobileSponsorCard 
                key={sponsor.id} 
                sponsor={sponsor} 
                index={index} 
              />
            ))}
            
            {filteredSponsors.length === 0 && !loading && (
              <div className="text-center py-12">
                <div className="text-muted-foreground text-4xl mb-4">🤝</div>
                <h3 className="text-lg font-semibold text-foreground mb-2 font-heading">
                  No Sponsors Found
                </h3>
                <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto font-body">
                  {sponsors.length === 0 
                    ? 'No sponsor data found in the database. Sponsors will appear here once users register.' 
                    : 'Try adjusting your search filters to find what you\'re looking for.'}
                </p>
                {sponsors.length > 0 && (
                  <button
                    onClick={() => setFilters({
                      sponsorId: '',
                      sponsorName: '',
                      memberId: '',
                      status: 'all'
                    })}
                    className="btn-electric px-4 py-2.5 text-sm rounded-lg transition-all font-body"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sponsor Details Modal */}
      {showDetailsModal && <SponsorDetailsModal />}
    </div>
  );
};

export default SponsorWiseList;
